import React, { useState, useRef, useEffect } from 'react';
import { ArrowUpFromLine, ClipboardCopy, FileDown, Check } from 'lucide-react';
import { Message, Assistant } from '@/types';
import { copyConversationToClipboard, downloadConversationAsPDF } from '@/utils/conversationExport';

interface ShareButtonProps {
    messages: Message[];
    assistant: Assistant;
}

export const ShareButton: React.FC<ShareButtonProps> = ({ messages, assistant }) => {
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    const handleCopy = async () => {
        const success = await copyConversationToClipboard(messages, assistant.name);
        if (success) {
            setCopied(true);
            setTimeout(() => {
                setCopied(false);
                setOpen(false);
            }, 1500);
        }
    };

    const handleDownloadPDF = () => {
        downloadConversationAsPDF(messages, assistant.name);
        setOpen(false);
    };

    if (messages.length === 0) return null;

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setOpen(!open)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-gray-300 hover:text-cyan-400 transition-colors rounded-lg hover:bg-white/5 text-sm"
                title="Share conversation"
            >
                <ArrowUpFromLine className="w-4 h-4" />
                <span className="hidden sm:inline">Share</span>
            </button>

            {open && (
                <div className="absolute right-0 top-full mt-2 w-52 bg-[#2a2a2a] border border-gray-600 rounded-lg shadow-xl z-50 overflow-hidden">
                    <button
                        onClick={handleCopy}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-200 hover:bg-white/10 transition-colors"
                    >
                        {copied ? (
                            <Check className="w-4 h-4 text-green-400" />
                        ) : (
                            <ClipboardCopy className="w-4 h-4 text-cyan-400" />
                        )}
                        {copied ? 'Copied!' : 'Copy to Clipboard'}
                    </button>
                    <button
                        onClick={handleDownloadPDF}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-200 hover:bg-white/10 transition-colors"
                    >
                        <FileDown className="w-4 h-4 text-cyan-400" />
                        Download as PDF
                    </button>
                </div>
            )}
        </div>
    );
};
